// react
import { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';

// context
import { ContextIfrPlan, ContextIfrPlanVersion, ContextProject } from '../Project.context';

// router
import { Link as RouterLink, useSearchParams } from 'react-router-dom';

// hooks
import { useFetch } from '../../../../hooks/useFetch';
import { useIfrPlanPrint } from '../../../../hooks/useIfrPlanPrint';

// i18n
import { useTranslation } from 'react-i18next';

// mui
import { BoxProps, Button, Chip, Container, Grid, PaletteBrand, Typography } from '@mui/material';
import { GridColDef, GridRowModel, GridValidRowModel } from '@mui/x-data-grid';

// icons
import { Print as IconPrint } from '@mui/icons-material';

// types
import {
	Account,
	CashInMethod,
	Category,
	Component,
	ErrorCause,
	IfrPlanVersionDataPayload,
	Loan,
} from '../../../../@types';
import { ifrMonthColumnNames } from '../../../../@types/unions';

// ui
import { Carousel } from '../../../ui/Carousel/Carousel';
import { ChipStatus } from '../../../ui/ChipStatus/ChipStatus';
import { Loading } from '../../../ui/Loading/Loading';

// pages
import { Details } from './Details/Details';
import { SelectIfrPlanVersion } from './SelectIfrPlanVersion/SelectIfrPlanVersion';

// cards
import { CardEmpty } from '../../../cards/CardEmpty/CardEmpty';
import { CardLoanLite } from '../../../cards/CardLoanLite/CardLoanLite';

// table
import { TableSheet } from '../../../ui/TableSheet/TableSheet';

// dialogs
import { DialogAddIfrPlanVersion } from '../../../dialogs/DialogAddIfrPlanVersion/DialogAddIfrPlanVersion';
import { DialogUpdateIfrPlanVersionStatus } from '../../../dialogs/DialogUpdateIfrPlanVersionStatus/DialogUpdateIfrPlanVersionStatus';

// theme
import { theme } from '../../../../theme';

// utils
import { getAllocation } from '../../../../utils/allocations';
import { formatCurrency } from '../../../../utils/currency';
import { cellWidths } from '../../../../utils/ifrs';

// props
export interface ProcessRowsDataProps {
	rows: readonly GridValidRowModel[];
}

export interface GetRowDataFromVersionDataProps {
	loanId: number;
	account?: number;
	cashInMethod?: number;
	category?: number;
	component?: number;
}

export interface HandleChangeDataProps {
	field: string;
	rowId: string | number;
	rowType: 'cashIn' | 'cashOutCategory' | 'cashOutComponent';
	value: number | undefined;
}

export type IfrRow = GridRowModel & {
	account?: number | undefined;
	cashInMethod?: number | undefined;
	category?: number | undefined;
	component?: number | undefined;
	loan?: Loan;
	month?: number;
	year?: number;
};

// constants
const colorPalettes: Record<string, keyof PaletteBrand> = {
	cashIn: 'blue',
	categories: 'blue',
	components: 'blue',
	check: 'grey',
};

export const IfrPlan: FC = () => {
	// hooks
	const [searchParams, setSearchParams] = useSearchParams();

	// params
	const paramDialog = searchParams.get('dialog');
	const paramIfrPlanVersion = searchParams.get('ifrPlanVersion');

	// context
	const { ifrPlan, refetchIfrPlan } = useContext(ContextIfrPlan);
	const { ifrPlanVersion, ifrPlanVersionPayload, setIfrPlanVersion, setIfrPlanVersionPayload } =
		useContext(ContextIfrPlanVersion);
	const { project } = useContext(ContextProject);

	// i18n
	const { t } = useTranslation();

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// state
	const [activeLoan, setActiveLoan] = useState<Loan | null>(null);
	const [cashInMethods, setCashInMethods] = useState<CashInMethod[]>([]);
	const [categories, setCategories] = useState<Category[]>([]);
	const [components, setComponents] = useState<Component[]>([]);
	const [designatedAccounts, setDesignatedAccounts] = useState<Account[]>([]);
	const [errors, setErrors] = useState<ErrorCause[]>([]);
	const [loans, setLoans] = useState<Loan[]>([]);

	// fetch loans
	const { isLoading: isLoadingLoans } = useFetch({
		isEnabled: Boolean(project?.id),
		url: `${process.env.REACT_APP_API_URL}/loans?project=${project?.id}&populate=entities,accounts&status=ENABLED`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res: { data?: Loan[] }) => {
			if (res.data) {
				setLoans(res.data);

				if (res.data.length) {
					setActiveLoan(res.data[0]);
				}
			}
		},
	});

	// fetch accounts
	const { isLoading: isLoadingDesignatedAccounts } = useFetch({
		isEnabled: Boolean(project?.id),
		url: `${process.env.REACT_APP_API_URL}/accounts?project=${project?.id}&type=DESIGNATED&populate=entities,loans`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res: { data?: Account[] }) => {
			if (res.data) {
				setDesignatedAccounts(res.data);
			}
		},
	});

	// fetch categories
	const { isLoading: isLoadingCategories } = useFetch({
		isEnabled: Boolean(project?.id),
		url: `${process.env.REACT_APP_API_URL}/categories?project=${project?.id}&status=ACTIVE&populate=allocations.conversions`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res: { data?: Category[] }) => {
			if (res.data) {
				setCategories(res.data);
			}
		},
	});

	// fetch components
	const { isLoading: isLoadingComponents } = useFetch({
		isEnabled: Boolean(project?.id),
		url: `${process.env.REACT_APP_API_URL}/components?project=${project?.id}&status=ACTIVE&populate=allocations.conversions`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res: { data?: Component[] }) => {
			if (res.data) {
				setComponents(res.data);
			}
		},
	});

	// fetch cash in methods
	const { isLoading: isLoadingCashInMethods } = useFetch({
		isEnabled: Boolean(project?.id),
		url: `${process.env.REACT_APP_API_URL}/cash-in-methods?project=${project?.id},null&status=ACTIVE`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res: { data?: CashInMethod[] }) => {
			if (res.data) {
				setCashInMethods(res.data);
			}
		},
	});

	// vars
	const hasColumnAllocation = false;
	const isDataLoading =
		isLoadingLoans ||
		isLoadingDesignatedAccounts ||
		isLoadingCashInMethods ||
		isLoadingCategories ||
		isLoadingComponents;

	const fieldsSticky = useMemo(() => {
		return {
			left: ['name'],
			right: ['total'],
		};
	}, []);

	const fieldsEditable = useMemo(() => {
		let fields: string[] = [];

		if (ifrPlanVersion?.status === 'DRAFT') {
			fields = [...ifrMonthColumnNames];
		}

		return fields;
	}, [ifrPlanVersion]);

	// Get color palette for table styling
	const getMonthBackgroundColor = ({
		field,
		palette,
	}: {
		field: string;
		palette: keyof PaletteBrand;
	}) => {
		const highlightMonths = ['month1', 'month2', 'month3', 'month7', 'month8', 'month9'];
		return highlightMonths.includes(field)
			? theme.palette.brand[palette][100] // Q1 & Q3
			: theme.palette.brand.grey[100]; // Q2 & Q4
	};

	// Returns BoxProps for headers, data rows, and total rows
	const getBoxPropsForPalette = useCallback(() => {
		const boxPropsRowHeaders: Record<string, Record<string, BoxProps>> = {};
		const boxPropsRowData: Record<string, Record<string, BoxProps>> = {};
		const boxPropsRowTotals: Record<string, Record<string, BoxProps>> = {};

		Object.keys(colorPalettes).forEach((key) => {
			const palette = colorPalettes[key];

			// Header row props
			boxPropsRowHeaders[key] = {
				name: {
					sx: {
						backgroundColor: theme.palette.common.white,
						justifyContent: 'flex-start',
					},
				},
				allocation: {
					sx: {
						backgroundColor: theme.palette.brand.grey[100],
					},
				},
				total: {
					sx: {
						backgroundColor: theme.palette.brand[palette][200],
						color: theme.palette.brand[palette][800],
					},
				},
			};

			// Data row props
			boxPropsRowData[key] = {
				allocation: {
					sx: {
						backgroundColor: theme.palette.brand.grey[100],
					},
				},
				name: {
					sx: {
						backgroundColor: theme.palette.common.white,
						justifyContent: 'flex-start',
					},
				},
				total: {
					sx: {
						backgroundColor: theme.palette.brand[palette][200],
						color: theme.palette.brand[palette][800],
					},
				},
			};

			// Total row props
			boxPropsRowTotals[key] = {
				name: {
					sx: {
						backgroundColor: theme.palette.brand[palette][200],
						color: theme.palette.brand[palette][800],
						justifyContent: 'flex-start',
					},
				},
				allocation: {
					sx: {
						backgroundColor: theme.palette.brand.blue[200],
						color: theme.palette.brand.blue[800],
					},
				},
				total: {
					sx: {
						backgroundColor: theme.palette.brand.blue[200],
						color: theme.palette.brand.blue[800],
					},
				},
			};

			ifrMonthColumnNames.forEach((month) => {
				// Header row month cells - alternating colors every 3 months
				boxPropsRowHeaders[key][month] = {
					sx: {
						backgroundColor: getMonthBackgroundColor({
							field: month,
							palette,
						}),
						justifyContent: 'center',
					},
				};

				// Data row month cells
				boxPropsRowData[key][month] = {
					sx: {
						justifyContent: 'flex-end',
					},
				};

				// Total row month cells
				boxPropsRowTotals[key][month] = {
					sx: {
						backgroundColor: theme.palette.brand[palette][200],
						color: theme.palette.brand[palette][800],
					},
				};
			});
		});

		return { boxPropsRowHeaders, boxPropsRowData, boxPropsRowTotals };
	}, []);

	const { boxPropsRowHeaders, boxPropsRowData, boxPropsRowTotals } = useMemo(() => {
		return getBoxPropsForPalette();
	}, [getBoxPropsForPalette]);

	// Create months mapping
	const fiscalYearMonthsMap = useMemo(() => {
		const months: Record<string, { name: string; month: number; year: number }> = {};

		if (ifrPlan?.dateFiscalYearStart && ifrPlan?.dateFiscalYearEnd) {
			// Parse the date string as UTC to avoid timezone issues
			// Add 'T00:00:00.000Z' to ensure it's treated as UTC if it's just a date
			const dateString = ifrPlan.dateFiscalYearStart.includes('T')
				? ifrPlan.dateFiscalYearStart
				: `${ifrPlan.dateFiscalYearStart}T00:00:00.000Z`;
			const startDate = new Date(dateString);
			const startMonth = startDate.getUTCMonth();
			const startYear = startDate.getUTCFullYear();

			// Create date formatter for localized month names using UTC
			const monthFormatter = new Intl.DateTimeFormat('en-US', {
				month: 'short',
				timeZone: 'UTC',
			});

			for (let i = 0; i < ifrMonthColumnNames.length; i++) {
				const monthColumn = ifrMonthColumnNames[i];
				// Use UTC methods to avoid timezone issues
				const date = new Date(Date.UTC(startYear, startMonth + i, 1));
				const monthName = monthFormatter.format(date);
				const year = date.getUTCFullYear();

				months[monthColumn] = {
					name: `${monthName} ${year}`,
					month: date.getUTCMonth() + 1,
					year,
				};
			}
		} else {
			// Fallback to default month labels and calendar year if fiscal year dates are not available
			for (let i = 0; i < ifrMonthColumnNames.length; i++) {
				const monthColumn = ifrMonthColumnNames[i];
				months[monthColumn] = {
					name: monthColumn.replace('month', `${t('month')} `),
					month: i + 1,
					year: new Date().getFullYear(),
				};
			}
		}

		return months;
	}, [ifrPlan?.dateFiscalYearStart, ifrPlan?.dateFiscalYearEnd, t]);

	const rowDataMonthHeaders = useMemo(() => {
		const months: Record<string, string> = {};

		for (let i = 0; i < Object.keys(fiscalYearMonthsMap).length; i++) {
			const key = ifrMonthColumnNames[i];
			months[key] = fiscalYearMonthsMap[key].name;
		}

		return months;
	}, [fiscalYearMonthsMap]);

	const rowDataEmpty = useMemo(() => {
		const row = ifrMonthColumnNames.reduce(
			(acc, month) => {
				acc[month] = 0;
				return acc;
			},
			{} as Record<string, number>
		);
		row.total = 0;
		return row;
	}, []);

	// All tables use same columns
	const getColumns = ({
		table,
	}: {
		table: 'cashIn' | 'cashOutCategory' | 'cashOutComponent' | 'checks';
	}) => {
		const columns: GridColDef[] = [{ field: 'name', headerName: 'Name', minWidth: cellWidths.lg }];

		columns.push(
			...ifrMonthColumnNames.map((col) => ({
				field: col,
				headerName: rowDataMonthHeaders[col],
				minWidth: cellWidths.sm,
			}))
		);

		if (hasColumnAllocation && ['cashOutCategory', 'cashOutComponent'].includes(table)) {
			columns.push({ field: 'allocation', headerName: 'Allocation', minWidth: cellWidths.md });
		}

		columns.push({ field: 'total', headerName: 'Total', minWidth: cellWidths.md });

		return columns;
	};

	// Helper function to convert version data back to row format for display
	const getRowDataFromVersionData = useCallback(
		({
			loanId,
			account,
			cashInMethod,
			category,
			component,
		}: GetRowDataFromVersionDataProps): Record<string, number> => {
			const rowData = { ...rowDataEmpty };

			if (!fiscalYearMonthsMap || !ifrPlanVersionPayload?.data?.length) {
				return rowData;
			}

			// Filter version data for this specific row
			const relevantData = ifrPlanVersionPayload.data.filter((data) => {
				if (data.loan !== loanId) return false;

				// Only one field can be defined at a time, so we check which one is set
				if (account !== undefined) {
					return data.account === account;
				}
				if (cashInMethod !== undefined) {
					return data.cashInMethod === cashInMethod;
				}
				if (category !== undefined) {
					return data.category === category;
				}
				if (component !== undefined) {
					return data.component === component;
				}

				// If none are defined, match entries where all relevant fields are null/undefined
				return (
					(data.account === null || data.account === undefined) &&
					(data.cashInMethod === null || data.cashInMethod === undefined) &&
					(data.category === null || data.category === undefined) &&
					(data.component === null || data.component === undefined)
				);
			});

			// Populate the row data with amounts from version data
			relevantData.forEach((data) => {
				// Find the column index that corresponds to this month/year
				const monthKey = ifrMonthColumnNames.find((monthField) => {
					const monthData = fiscalYearMonthsMap[monthField];
					return monthData && monthData.month === data.month && monthData.year === data.year;
				});

				if (monthKey) {
					rowData[monthKey] = (rowData[monthKey] || 0) + data.amount;
				}
			});

			// Recalculate total
			rowData.total = ifrMonthColumnNames.reduce((sum, monthField) => {
				return sum + (rowData[monthField] || 0);
			}, 0);

			return rowData;
		},
		[fiscalYearMonthsMap, ifrPlanVersionPayload, rowDataEmpty]
	);

	// Handle data changes from the table
	const handleChangeData = ({ field, rowId, rowType, value }: HandleChangeDataProps) => {
		if (!ifrPlanVersionPayload?.data || !activeLoan) {
			return;
		}

		// Define rows
		let rows = rowsCashIn;
		if (rowType === 'cashIn') {
			rows = rowsCashIn;
		} else if (rowType === 'cashOutCategory') {
			rows = rowsCashOutCategory;
		} else if (rowType === 'cashOutComponent') {
			rows = rowsCashOutComponent;
		}

		// Find target row
		const targetRow = rows.find((row) => row.id === rowId);
		if (!targetRow) {
			return;
		}

		const loan = targetRow.loan as Loan;

		// Extract the month and year from the field name
		const monthData = fiscalYearMonthsMap[field];
		if (!monthData || !ifrPlan?.currency) {
			console.warn('Invalid field or missing currency:', field);
			return;
		}

		// Clone the current payload data
		const updatedData: IfrPlanVersionDataPayload[] = [...ifrPlanVersionPayload.data];

		const existingIndex = updatedData.findIndex((item) => {
			// First check loan, month, and year
			if (item.loan !== loan.id || item.month !== monthData.month || item.year !== monthData.year) {
				return false;
			}

			// Then check the identifying fields using the same logic as getRowDataFromVersionData
			// Only one field can be defined at a time
			const targetAccount = targetRow.account as number | undefined;
			const targetCashInMethod = targetRow.cashInMethod as number | undefined;
			const targetCategory = targetRow.category as number | undefined;
			const targetComponent = targetRow.component as number | undefined;

			if (targetAccount !== undefined) {
				return item.account === targetAccount;
			}
			if (targetCashInMethod !== undefined) {
				return item.cashInMethod === targetCashInMethod;
			}
			if (targetCategory !== undefined) {
				return item.category === targetCategory;
			}
			if (targetComponent !== undefined) {
				return item.component === targetComponent;
			}

			// If none are defined, match entries where all relevant fields are null/undefined
			return (
				(item.account === null || item.account === undefined) &&
				(item.cashInMethod === null || item.cashInMethod === undefined) &&
				(item.category === null || item.category === undefined) &&
				(item.component === null || item.component === undefined)
			);
		});

		if (existingIndex >= 0) {
			// Update existing item
			if (value && value > 0) {
				updatedData[existingIndex] = {
					...updatedData[existingIndex],
					amount: value,
				};
			} else {
				// Remove item if value is 0 or undefined
				updatedData.splice(existingIndex, 1);
			}
		} else if (ifrPlan?.currency && value && value > 0) {
			// Create new item only if value is greater than 0
			const newItem: IfrPlanVersionDataPayload = {
				account: targetRow.account as number | undefined,
				amount: value,
				cashInMethod: targetRow.cashInMethod as number | undefined,
				category: targetRow.category as number | undefined,
				component: targetRow.component as number | undefined,
				currency: ifrPlan.currency,
				loan: loan.id,
				month: monthData.month,
				year: monthData.year,
			};
			updatedData.push(newItem);
		}

		setIfrPlanVersionPayload({
			data: updatedData,
		});
	};

	// Derive rowsCashIn from ifrPlanVersionPayload.data
	const rowsCashIn = useMemo(() => {
		if (!loans.length || !ifrPlanVersionPayload?.data) {
			return [];
		}
		const rows: IfrRow[] = [];

		loans.forEach((loan) => {
			// Add header row
			rows.push({
				BoxProps: boxPropsRowHeaders.cashIn,
				fieldsSticky,
				id: `cash-in-headers-loan-${loan.id}`,
				name: '',
				loan,
				total: t('total'),
				...rowDataMonthHeaders,
			});

			// Add designated account rows
			designatedAccounts
				.sort((a, b) => a.name.localeCompare(b.name))
				.forEach((account) => {
					const populatedData = getRowDataFromVersionData({
						loanId: loan.id,
						account: account.id,
						cashInMethod: undefined,
					});

					rows.push({
						account: account.id,
						BoxProps: boxPropsRowData.cashIn,
						cashInMethod: undefined,
						fieldsEditable,
						fieldsSticky,
						id: `account-${account.id}-loan-${loan.id}`,
						name: t('advancesTo', { name: account.name }),
						loan,
						...populatedData,
					});
				});

			// Add cash-in method rows
			cashInMethods
				.sort((a, b) => a.name.localeCompare(b.name))
				.forEach((cashInMethod) => {
					const populatedData = getRowDataFromVersionData({
						loanId: loan.id,
						account: undefined,
						cashInMethod: cashInMethod.id,
					});

					rows.push({
						account: undefined,
						BoxProps: boxPropsRowData.cashIn,
						cashInMethod: cashInMethod.id,
						fieldsEditable,
						fieldsSticky,
						id: `cash-in-method-${cashInMethod.id}-loan-${loan.id}`,
						name: cashInMethod.name,
						loan,
						...populatedData,
					});
				});

			// Add total row (calculated from individual rows)
			const totalData = { ...rowDataEmpty };
			rows
				.filter((row) => row.loan?.id === loan.id)
				.forEach((row) => {
					ifrMonthColumnNames.forEach((monthField) => {
						if (typeof row[monthField] === 'number') {
							totalData[monthField] += row[monthField] || 0;
						}
					});
				});
			totalData.total = ifrMonthColumnNames.reduce((sum, monthField) => {
				return sum + totalData[monthField];
			}, 0);

			rows.push({
				BoxProps: boxPropsRowTotals.cashIn,
				fieldsSticky,
				id: `cash-in-totals-loan-${loan.id}`,
				name: t('total'),
				loan,
				...totalData,
			});
		});

		return rows;
	}, [
		boxPropsRowData,
		boxPropsRowHeaders,
		boxPropsRowTotals,
		cashInMethods,
		designatedAccounts,
		fieldsEditable,
		fieldsSticky,
		getRowDataFromVersionData,
		ifrPlanVersionPayload,
		loans,
		rowDataEmpty,
		rowDataMonthHeaders,
		t,
	]);

	// Derive rowsCashOutCategory from ifrPlanVersionPayload.data
	const rowsCashOutCategory = useMemo(() => {
		if (!loans.length || !ifrPlanVersionPayload?.data) {
			return [];
		}
		const rows: IfrRow[] = [];

		loans.forEach((loan) => {
			// Add header row
			rows.push({
				allocation: t('allocation'),
				BoxProps: boxPropsRowHeaders.categories,
				fieldsSticky,
				id: `category-headers-loan-${loan.id}`,
				name: '',
				loan,
				total: t('total'),
				...rowDataMonthHeaders,
			});

			// Add category rows
			categories
				.sort((a, b) => a.name.localeCompare(b.name))
				.forEach((category, index) => {
					const populatedData = getRowDataFromVersionData({
						loanId: loan.id,
						category: category.id,
					});

					rows.push({
						allocation: category.allocations
							? getAllocation({
									allocations: category.allocations,
									currency: ifrPlan?.currency,
									loanId: loan.id,
								})?.amount
							: undefined,
						BoxProps: boxPropsRowData.categories,
						category: category.id,
						fieldsEditable,
						fieldsSticky,
						id: `category-${category.id}-loan-${loan.id}`,
						index,
						name: category.name,
						loan,
						...populatedData,
					});
				});

			// Add total row (calculated from individual rows)
			const totalData = { ...rowDataEmpty };
			const rowsForLoan = rows.filter((row) => row.loan?.id === loan.id);

			rowsForLoan.forEach((row) => {
				ifrMonthColumnNames.forEach((monthField) => {
					if (typeof row[monthField] === 'number') {
						totalData[monthField] += row[monthField] || 0;
					}
				});
			});

			totalData.allocation = rowsForLoan.reduce((sum, row) => {
				return sum + (typeof row.allocation === 'number' ? row.allocation : 0);
			}, 0);

			totalData.total = ifrMonthColumnNames.reduce((sum, monthField) => {
				return sum + totalData[monthField];
			}, 0);

			rows.push({
				BoxProps: boxPropsRowTotals.categories,
				fieldsSticky,
				id: `category-totals-loan-${loan.id}`,
				name: t('total'),
				loan,
				...totalData,
			});
		});

		return rows;
	}, [
		boxPropsRowData,
		boxPropsRowHeaders,
		boxPropsRowTotals,
		categories,
		fieldsEditable,
		fieldsSticky,
		getRowDataFromVersionData,
		ifrPlan?.currency,
		ifrPlanVersionPayload,
		loans,
		rowDataEmpty,
		rowDataMonthHeaders,
		t,
	]);

	// Derive rowsCashOutComponent from ifrPlanVersionPayload.data
	const rowsCashOutComponent = useMemo(() => {
		if (!loans.length || !ifrPlanVersionPayload?.data) {
			return [];
		}

		const rows: IfrRow[] = [];

		loans.forEach((loan) => {
			// Add header row
			rows.push({
				allocation: t('allocation'),
				BoxProps: boxPropsRowHeaders.components,
				fieldsSticky,
				id: `component-headers-loan-${loan.id}`,
				name: '',
				loan,
				total: t('total'),
				...rowDataMonthHeaders,
			});

			// Add component rows
			components
				.sort((a, b) => a.name.localeCompare(b.name))
				.forEach((component) => {
					const populatedData = getRowDataFromVersionData({
						loanId: loan.id,
						component: component.id,
					});

					rows.push({
						allocation: component.allocations
							? getAllocation({
									allocations: component.allocations,
									currency: ifrPlan?.currency,
									loanId: loan.id,
								})?.amount
							: undefined,
						BoxProps: boxPropsRowData.components,
						component: component.id,
						fieldsEditable,
						fieldsSticky,
						id: `component-${component.id}-loan-${loan.id}`,
						name: component.name,
						loan,
						...populatedData,
					});
				});

			// Add total row (calculated from individual rows)
			const totalData = { ...rowDataEmpty };
			const rowsForLoan = rows.filter((row) => row.loan?.id === loan.id);

			rowsForLoan.forEach((row) => {
				ifrMonthColumnNames.forEach((monthField) => {
					if (typeof row[monthField] === 'number') {
						totalData[monthField] += row[monthField] || 0;
					}
				});
			});

			totalData.allocation = rowsForLoan.reduce((sum, row) => {
				return sum + (typeof row.allocation === 'number' ? row.allocation : 0);
			}, 0);

			totalData.total = ifrMonthColumnNames.reduce((sum, monthField) => {
				return sum + totalData[monthField];
			}, 0);

			rows.push({
				BoxProps: boxPropsRowTotals.components,
				fieldsSticky,
				id: `component-totals-loan-${loan.id}`,
				name: t('total'),
				loan,
				...totalData,
			});
		});

		return rows;
	}, [
		boxPropsRowData,
		boxPropsRowHeaders,
		boxPropsRowTotals,
		components,
		fieldsEditable,
		fieldsSticky,
		getRowDataFromVersionData,
		ifrPlan?.currency,
		ifrPlanVersionPayload,
		loans,
		rowDataEmpty,
		rowDataMonthHeaders,
		t,
	]);

	// Calculate check table to ensure categories and components are equal
	const rowsCheck = useMemo(() => {
		const rows: IfrRow[] = [];

		loans.forEach((loan) => {
			// Get totals from category table
			const categoryTotalRow = rowsCashOutCategory.find(
				(row) => row.id === `category-totals-loan-${loan.id}` && row.loan?.id === loan.id
			);
			const categoryTotals = categoryTotalRow || rowDataEmpty;

			// Get totals from component table
			const componentTotalRow = rowsCashOutComponent.find(
				(row) => row.id === `component-totals-loan-${loan.id}` && (row.loan as Loan).id === loan.id
			);
			const componentTotals = componentTotalRow || rowDataEmpty;

			// Calculate differences
			const checkRow: Record<string, unknown> = {
				BoxProps: boxPropsRowData.check,
				fieldsSticky,
				id: `checks`,
				name: t('ifrPlan.categoryComponentDifference'),
				loan,
				...rowDataEmpty,
			};

			ifrMonthColumnNames.forEach((monthField) => {
				const categoryValue =
					((categoryTotals as Record<string, unknown>)[monthField] as number) || 0;
				const componentValue =
					((componentTotals as Record<string, unknown>)[monthField] as number) || 0;
				const difference = categoryValue - componentValue;
				(checkRow as Record<string, unknown>)[monthField] = difference;

				if (difference !== 0) {
					checkRow.BoxProps = {
						...(checkRow.BoxProps as object),
						[monthField]: {
							sx: {
								...(checkRow.BoxProps as Record<string, BoxProps>)[monthField]?.sx,
								backgroundColor: theme.palette.brand.orange[100],
							},
						},
					};
				}
			});

			// Calculate total difference
			checkRow.total = ifrMonthColumnNames.reduce((sum, monthField) => {
				const value = ((checkRow as Record<string, unknown>)[monthField] as number) || 0;
				return sum + value;
			}, 0);

			// Add header row
			const headerRow = {
				BoxProps: boxPropsRowHeaders.check,
				fieldsSticky,
				id: `checks-headers`,
				name: '',
				loan,
				total: 'Total',
				...rowDataMonthHeaders,
			};
			rows.push(headerRow, checkRow);
		});

		return rows;
	}, [
		boxPropsRowData,
		boxPropsRowHeaders,
		fieldsSticky,
		loans,
		rowsCashOutCategory,
		rowsCashOutComponent,
		rowDataEmpty,
		rowDataMonthHeaders,
		t,
	]);

	// Print functionality
	const { handlePrint } = useIfrPlanPrint({
		ifrPlan,
		ifrPlanVersion,
		project,
		loans,
		rowsCashIn,
		rowsCashOutCategory,
		rowsCashOutComponent,
		rowsCheck,
		rowDataMonthHeaders,
	});

	// Collect errors
	useEffect(() => {
		const errorsNew: ErrorCause[] = [];

		const boxPropsTotal = {
			total: {
				sx: {
					backgroundColor: theme.palette.brand.orange[100],
				},
			},
		};

		// Check category totals that exceed allocation (sum across all loans)
		categories.forEach((category) => {
			if (category.allocations) {
				const categoryAllocation = category.allocations.find(
					(a) => a.currency === ifrPlan?.currency
				);

				if (categoryAllocation) {
					// Sum category totals across all loans
					let categoryTotalAcrossLoans = 0;
					const categoryLoanRows: { loan: Loan; row: GridRowModel; total: number }[] = [];

					loans.forEach((loan) => {
						const categoryLoanRow = rowsCashOutCategory.find(
							(row) => row.id === `category-${category.id}-loan-${loan.id}`
						);

						if (categoryLoanRow) {
							categoryTotalAcrossLoans += categoryLoanRow.total || 0;
							categoryLoanRows.push({
								loan,
								row: categoryLoanRow,
								total: categoryLoanRow.total || 0,
							});
						}
					});

					if (categoryTotalAcrossLoans > categoryAllocation.amount) {
						// Highlight all affected loan rows
						categoryLoanRows.forEach(({ row }) => {
							row.BoxProps = {
								...(row.BoxProps as object),
								...boxPropsTotal,
							};
						});

						// Add error to validation errors (once per category, not per loan)
						errorsNew.push({
							id: 'IFR_PLAN_CATEGORY_ALLOCATION_EXCEEDED',
							message: `Category '${category.name}' total planned amount across all loans exceeds allocation`,
							details: {
								category,
								loans: categoryLoanRows.map(({ loan, total }) => ({ loan, planned: total })),
								totalPlanned: categoryTotalAcrossLoans,
								allocated: categoryAllocation.amount,
							},
						});
					}
				}
			}
		});

		// Check component totals that exceed allocation (sum across all loans)
		components.forEach((component) => {
			if (component.allocations) {
				const componentAllocation = component.allocations.find(
					(a) => a.currency === ifrPlan?.currency
				);

				if (componentAllocation) {
					// Sum component totals across all loans
					let componentTotalAcrossLoans = 0;
					const componentLoanRows: { loan: Loan; row: GridRowModel; total: number }[] = [];

					loans.forEach((loan) => {
						const componentLoanRow = rowsCashOutComponent.find(
							(row) => row.id === `component-${component.id}-loan-${loan.id}`
						);

						if (componentLoanRow) {
							componentTotalAcrossLoans += componentLoanRow.total || 0;
							componentLoanRows.push({
								loan,
								row: componentLoanRow,
								total: componentLoanRow.total || 0,
							});
						}
					});

					if (componentTotalAcrossLoans > componentAllocation.amount) {
						// Highlight all affected loan rows
						componentLoanRows.forEach(({ row }) => {
							row.BoxProps = {
								...(row.BoxProps as object),
								...boxPropsTotal,
							};
						});

						// Add error to validation errors (once per component, not per loan)
						errorsNew.push({
							id: 'IFR_PLAN_COMPONENT_ALLOCATION_EXCEEDED',
							message: `Component '${component.name}' total planned amount across all loans exceeds allocation`,
							details: {
								component,
								loans: componentLoanRows.map(({ loan, total }) => ({ loan, planned: total })),
								totalPlanned: componentTotalAcrossLoans,
								allocated: componentAllocation.amount,
							},
						});
					}
				}
			}
		});

		loans.forEach((loan) => {
			// Highlight cash out totals that exceed cash in totals
			const cashInLoanTotalRow = rowsCashIn.find(
				(row) => row.id === `cash-in-totals-loan-${loan.id}`
			);

			const cashOutCategoryLoanTotalRow = rowsCashOutCategory.find(
				(row) => row.id === `category-totals-loan-${loan.id}`
			);

			const cashOutComponentLoanTotalRow = rowsCashOutComponent.find(
				(row) => row.id === `component-totals-loan-${loan.id}`
			);

			if (cashInLoanTotalRow) {
				// Check if total cash in exceeds loan amount
				let loanAmountForCurrency: null | number = loan.amount;

				if (loan.currency !== ifrPlan?.currency) {
					const conversionAmount = loan.conversions?.find(
						(conversion) =>
							conversion.currency === ifrPlan?.currency && conversion.type === 'AMOUNT'
					);
					loanAmountForCurrency = conversionAmount?.amount || null;
				}

				if (loanAmountForCurrency && cashInLoanTotalRow.total > loanAmountForCurrency) {
					errorsNew.push({
						id: 'IFR_PLAN_CASH_IN_EXCEEDS_LOAN_AMOUNT',
						message: `Total cash in exceeds amount of ${formatCurrency({
							currency: ifrPlan?.currency,
							value: loanAmountForCurrency,
						})} for ${loan.code}.`,
						details: {
							loan,
							loanAmount: loanAmountForCurrency,
							cashInTotal: cashInLoanTotalRow.total,
						},
					});
				}

				ifrMonthColumnNames.forEach((monthField) => {
					const boxPropsMonthField = {
						[monthField]: {
							sx: {
								backgroundColor: theme.palette.brand.orange[100],
							},
						},
					};

					const monthData = fiscalYearMonthsMap[monthField];

					// Highlight cash out category total if it exceeds cash in total
					if (
						cashOutCategoryLoanTotalRow &&
						cashOutCategoryLoanTotalRow[monthField] > cashInLoanTotalRow[monthField]
					) {
						cashOutCategoryLoanTotalRow.BoxProps = {
							...(cashOutCategoryLoanTotalRow.BoxProps as object),
							...boxPropsMonthField,
						};

						// Add error to validation errors
						errorsNew.push({
							id: 'IFR_PLAN_CASH_OUT_CATEGORY_MONTH_EXCEEDS_CASH_IN',
							message: `Cash out categories exceed cash in for ${monthData?.name || monthField}`,
							details: {
								field: monthField,
								loan,
								month: monthData,
								cashIn: cashInLoanTotalRow[monthField],
								cashOutCategory: cashOutCategoryLoanTotalRow[monthField],
							},
						});
					}

					// Highlight cash out component total if it exceeds cash in total
					if (
						cashOutComponentLoanTotalRow &&
						cashOutComponentLoanTotalRow[monthField] > cashInLoanTotalRow[monthField]
					) {
						cashOutComponentLoanTotalRow.BoxProps = {
							...(cashOutComponentLoanTotalRow.BoxProps as object),
							...boxPropsMonthField,
						};

						// Add error to validation errors
						errorsNew.push({
							id: 'IFR_PLAN_CASH_OUT_COMPONENT_MONTH_EXCEEDS_CASH_IN',
							message: `Cash out components exceed cash in for ${monthData?.name || monthField}`,
							details: {
								field: monthField,
								loan,
								month: monthData,
								cashIn: cashInLoanTotalRow[monthField],
								cashOutComponent: cashOutComponentLoanTotalRow[monthField],
							},
						});
					}
				});
			}
		});

		// Update errors state
		setErrors(errorsNew);
	}, [
		categories,
		components,
		fiscalYearMonthsMap,
		ifrPlan?.currency,
		loans,
		rowsCashIn,
		rowsCashOutCategory,
		rowsCashOutComponent,
	]);

	// Initialize ifrPlanVersionPayload from ifrPlanVersion when it loads
	useEffect(() => {
		if (ifrPlanVersion?.data) {
			// Transform IfrPlanVersionData[] to IfrPlanVersionDataPayload[]
			const payloadData: IfrPlanVersionDataPayload[] = ifrPlanVersion.data.map((item) => ({
				account: item.account,
				amount: item.amount,
				cashInMethod: item.cashInMethod,
				category: item.category,
				component: item.component,
				currency: item.currency,
				loan: item.loan,
				month: item.month,
				year: item.year,
			}));

			setIfrPlanVersionPayload({
				data: payloadData,
			});
		}
	}, [ifrPlanVersion, setIfrPlanVersionPayload]);

	// update ifrPlanVersion if ifrPlanVersion param changes
	useEffect(() => {
		if (paramIfrPlanVersion && Number(paramIfrPlanVersion) !== ifrPlanVersion?.id) {
			const version = ifrPlan?.versions.find((v) => v.id === Number(paramIfrPlanVersion));
			if (version) {
				console.log('Param new version:', version);
				setIfrPlanVersion(version);
			}
		}
	}, [ifrPlan, ifrPlanVersion, paramIfrPlanVersion, setIfrPlanVersion]);

	if (isDataLoading) {
		return <Loading variant="page" />;
	}

	let content = (
		<Grid item xs={12}>
			<CardEmpty
				empty={{
					actions: [
						{
							children: t('seeFinancingSources'),
							component: RouterLink,
							to: `/project/${project?.idWbg}/financing`,
							variant: 'contained',
						},
					],
					content: {
						children: t('empty.ifr.content'),
					},
					IconCircleProps: {
						color: 'grey',
						type: 'plan',
					},
					title: {
						children: t('empty.ifrPlan.title'),
					},
				}}
			/>
		</Grid>
	);

	if (loans.length > 0) {
		content = (
			<>
				<Grid item xs={12}>
					<Grid
						alignItems="stretch"
						container
						direction={{ xs: 'column', sm: 'row' }}
						spacing={2}
						sx={{ display: 'flex', height: '100%', flexWrap: { lg: 'nowrap' } }}>
						<Grid item xs={12} lg={4}>
							<Details ifrPlan={ifrPlan} />
						</Grid>
						<Grid item xs={12} lg={8} sx={{ minWidth: 0 }}>
							<Carousel
								children={loans.map((loan, i) => (
									<CardLoanLite
										key={`carousel-item-${i}`}
										isActive={activeLoan?.id === loan.id}
										loan={loan}
										onClick={() => setActiveLoan(loan)}
									/>
								))}
								childrenVisible={loans.length === 1 ? 1 : 2}
								onNext={(child) => {
									setActiveLoan(child.props.loan);
								}}
								onPrev={(child) => {
									setActiveLoan(child.props.loan);
								}}
							/>
						</Grid>
					</Grid>
				</Grid>
				<Grid item xs={12}>
					<Typography variant="h3">
						<span>{t('planFor')}</span>
						<Chip
							className="tag"
							color="teal"
							label={activeLoan?.code}
							sx={{ fontWeight: 600, ml: 0.5 }}
							variant="twoTone"
						/>
					</Typography>
				</Grid>
				<Grid item xs={12}>
					<TableSheet
						columns={getColumns({ table: 'cashIn' })}
						defaultExpanded={true}
						label={t('tableSheets.cashIn.title')}
						onDataChange={(row, field, value) =>
							handleChangeData({ rowId: row.id, field, value, rowType: 'cashIn' })
						}
						palette="blue"
						rows={rowsCashIn.filter((row) => (row.loan as Loan).id === activeLoan?.id)}
					/>
				</Grid>
				<Grid item xs={12}>
					<TableSheet
						columns={getColumns({ table: 'cashOutCategory' })}
						defaultExpanded={true}
						label={t('tableSheets.cashOutCategories.title')}
						onDataChange={(row, field, value) =>
							handleChangeData({ rowId: row.id, field, value, rowType: 'cashOutCategory' })
						}
						palette="blue"
						rows={rowsCashOutCategory.filter((row) => (row.loan as Loan).id === activeLoan?.id)}
					/>
				</Grid>
				<Grid item xs={12}>
					<TableSheet
						columns={getColumns({ table: 'cashOutComponent' })}
						defaultExpanded={true}
						label={t('tableSheets.cashOutComponents.title')}
						onDataChange={(row, field, value) =>
							handleChangeData({ rowId: row.id, field, value, rowType: 'cashOutComponent' })
						}
						palette="blue"
						rows={rowsCashOutComponent.filter((row) => (row.loan as Loan).id === activeLoan?.id)}
					/>
				</Grid>
				<Grid item xs={12}>
					<TableSheet
						columns={getColumns({ table: 'checks' })}
						isExpandable={false}
						label={t('tableSheets.checks.title')}
						palette="grey"
						rows={rowsCheck.filter((row) => (row.loan as Loan).id === activeLoan?.id)}
					/>
				</Grid>
			</>
		);
	}

	return (
		<>
			<Container maxWidth={false}>
				<Grid container spacing={2}>
					<Grid item xs={12}>
						<Grid alignItems="flex-end" container spacing={1}>
							<Grid item>
								<Typography sx={{ lineHeight: '2rem' }} variant="h2">
									{ifrPlan?.name}
								</Typography>
							</Grid>
							{ifrPlanVersion?.status && (
								<Grid item>
									<ChipStatus status={ifrPlanVersion.status} />
								</Grid>
							)}
							<Grid item xs={12} ml="auto" md="auto">
								<Grid container spacing={2}>
									<Grid item>
										<SelectIfrPlanVersion />
									</Grid>
									<Grid item>
										<Button
											disabled={!loans.length}
											startIcon={<IconPrint />}
											onClick={handlePrint}
											variant="outlined">
											{t('print')}
										</Button>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
					{content}
				</Grid>
			</Container>
			<DialogAddIfrPlanVersion
				isOpen={paramDialog === 'editPlan'}
				ifrPlanVersionPayload={ifrPlanVersionPayload}
				onClose={() => {
					searchParams.delete('dialog');
					setSearchParams(searchParams);
				}}
				onSuccess={({ ifrPlanVersion: ifrPlanVersionUpdated }) => {
					// set ifrPlanVersion in params
					searchParams.set('ifrPlanVersion', String(ifrPlanVersionUpdated.id));
					setSearchParams(searchParams);

					// refetch ifr plan
					refetchIfrPlan();

					// set ifr plan version
					setIfrPlanVersion(ifrPlanVersionUpdated);
				}}
			/>
			<DialogUpdateIfrPlanVersionStatus
				errors={errors}
				isOpen={paramDialog === 'updateIfrPlanVersionStatus'}
				onClose={() => {
					searchParams.delete('dialog');
					searchParams.delete('status');
					setSearchParams(searchParams);
				}}
				onSuccess={({ ifrPlanVersion: ifrPlanVersionUpdated }) => {
					// refetch ifr plan
					refetchIfrPlan();

					// set ifr plan version
					setIfrPlanVersion(ifrPlanVersionUpdated);
				}}
			/>
		</>
	);
};
